import { Typography } from '@material-ui/core';
import { ThumbUp } from '@material-ui/icons';
import {
  Column,
  Columns,
  Inline,
  OverflowText,
  Stack,
  useUID,
} from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { formatNumber } from 'shared/helpers/IntlHelpers';
import { ShipperAvatar } from 'shared/modules/shipper/ShipperAvatar';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import styled from 'styled-components';
import { ShipperProfileLink } from '../shipper/ShipperProfileLink';
import { UnratedShipperDTO } from './data/CarrierRatingsDTO';
import { ShipperRatingAvailableTimeLeft } from './ShipperRatingAvailableTimeLeft';

const StyledThumbUp = styled(ThumbUp)`
  font-size: 16px;
`;

interface Props {
  unratedShipper: UnratedShipperDTO;
  rateButtonText?: string;
  onClickRate: () => void;
}

export function UnratedShipper({
  unratedShipper,
  rateButtonText = 'Rate Shipper',
  onClickRate,
}: Props) {
  const uid = useUID();
  const { data: carrierSettings } = useCarrierSettings();
  const isRateAllowed =
    carrierSettings?.carrier.verification_status === 'approved';

  return (
    <Columns
      aria-labelledby={uid}
      key={unratedShipper.guid}
      space="small"
      align="top"
    >
      <Column width="content">
        <ShipperAvatar
          name={unratedShipper.name}
          logo={unratedShipper.logo_url}
          size="small"
        />
      </Column>

      <Column>
        <Columns space="small" align={['top', 'center']} collapseBelow="tablet">
          <Column>
            <Stack space="xsmall">
              <Columns
                space="xsmall"
                align={['top', 'center']}
                collapseBelow="tablet"
              >
                <Column width={['fluid', 'adaptive']}>
                  <ShipperProfileLink
                    id={uid}
                    aria-label="shipper name"
                    guid={unratedShipper.guid}
                    medium="Ratings To Give"
                  >
                    <OverflowText title={unratedShipper.name}>
                      {unratedShipper.name}
                    </OverflowText>
                  </ShipperProfileLink>
                </Column>

                <Column width="content">
                  <Inline space="xxsmall" verticalAlign="center" noWrap={true}>
                    <StyledThumbUp color="action" />

                    <ShipperProfileLink
                      aria-label="rating percentage"
                      guid={unratedShipper.guid}
                      medium="Ratings To Give"
                      tab="ratings"
                    >
                      {unratedShipper.overall_rating || 0}%
                    </ShipperProfileLink>

                    <Typography
                      aria-label="ratings count"
                      color="textSecondary"
                      component="span"
                    >
                      ({formatNumber(unratedShipper.total_rating_count || 0)})
                    </Typography>
                  </Inline>
                </Column>
              </Columns>

              <Typography color="textSecondary">
                {unratedShipper.accepted_loads_count} loads accepted by you
              </Typography>
            </Stack>
          </Column>

          <Column width="content">
            <Stack align={['left', 'center']} space="xxsmall">
              <Button
                variant="neutral"
                onClick={onClickRate}
                disabled={!isRateAllowed}
              >
                {rateButtonText}
              </Button>
              <ShipperRatingAvailableTimeLeft
                date={unratedShipper.last_order_completed_date}
              />
            </Stack>
          </Column>
        </Columns>
      </Column>
    </Columns>
  );
}
